import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Loader2, UserCheck } from "lucide-react";
import { track } from "../lib/events";
import { ghostButtonClass, Status } from "../components/ui";
import { FormModal } from "../components/FormModal";
import { AttendanceTally } from "../attendance/AttendanceTally";
import { RosterRow } from "../attendance/RosterRow";
import { useAttendance } from "../attendance/useAttendance";
import { useOutbox } from "../attendance/useOutbox";
import { flushOutbox, outboxFor, queueAttendance, retryEntry } from "../lib/outbox";
import { usePDDayNumber } from "../pddays/usePDDays";
import { pdDayDetailPath } from "../lib/pdDayRoutes";
import type { AbsenceReasonCode, AttendanceStatus, RosterEntry } from "../types";

type Draft = Record<string, "present">;
const absenceReasons: AbsenceReasonCode[] = [
  "illness",
  "family_emergency",
  "transport",
  "official_duty",
  "other",
];

export function Attendance() {
  const { t } = useTranslation();
  const { sessionID = "" } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAttendance(sessionID);

  // Present marks are submitted together. An absent mark is submitted with
  // its reason from the modal, so its status and explanation travel together.
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState<AbsenceReasonCode | "">("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [savingReason, setSavingReason] = useState(false);
  const outbox = useOutbox(sessionID);
  const dayNumber = usePDDayNumber(sessionID);

  const roster = useMemo(() => data?.roster ?? [], [data]);
  const reasonEntry = roster.find((entry) => entry.teacher_id === reasonFor);
  const locked = data?.detail.status === "ended";

  // A mark is final once submitted: a row the server has recorded, or one
  // already handed to the outbox, can no longer be changed.
  function submitted(entry: RosterEntry): boolean {
    return (
      entry.attendance === "present" ||
      entry.attendance === "absent" ||
      outbox.has(entry.teacher_id)
    );
  }

  function statusOf(teacherID: string, original: string): AttendanceStatus {
    return draft[teacherID] ?? outbox.get(teacherID)?.status ?? (original as AttendanceStatus);
  }

  const pending = roster.filter(
    (entry) =>
      draft[entry.teacher_id] &&
      draft[entry.teacher_id] !== (outbox.get(entry.teacher_id)?.status ?? entry.attendance)
  );
  const failedEntries = [...outbox.values()].filter((entry) => entry.state === "failed");
  const inFlightCount = [...outbox.values()].filter((entry) => entry.state !== "failed").length;
  const present = roster.filter((e) => statusOf(e.teacher_id, e.attendance) === "present").length;
  const absent = roster.filter((e) => statusOf(e.teacher_id, e.attendance) === "absent").length;
  const unmarked = roster.length - present - absent;

  // Submitting hands the marks to the durable outbox rather than firing
  // PUTs inline: the queue survives a reload, retries on its own, and
  // reports per-teacher delivery state back to the roster.
  async function submit() {
    setSaving(true);
    queueAttendance(
      pending.map((entry) => ({
        sessionID,
        teacherID: entry.teacher_id,
        teacherName: entry.name || entry.assigned_id,
        status: draft[entry.teacher_id],
      }))
    );
    track("attendance_submitted", {
      payload: { session_id: sessionID, queued: pending.length },
    });
    setDraft({});
    await flushOutbox();
    await reload();
    setSaving(false);
  }

  function openAbsenceReason(entry: RosterEntry) {
    const queued = outbox.get(entry.teacher_id);
    setReasonFor(entry.teacher_id);
    setReasonCode(
      queued?.status === "absent" ? queued.reasonCode || "" : entry.absence_reason_code || ""
    );
    setReasonDetail(
      queued?.status === "absent" ? queued.reasonDetail || "" : entry.absence_reason_detail || ""
    );
    setReasonError("");
  }

  async function submitAbsenceReason() {
    if (!reasonEntry || !reasonCode || (reasonCode === "other" && !reasonDetail.trim())) return;
    setSavingReason(true);
    setReasonError("");
    try {
      queueAttendance([
        {
          sessionID,
          teacherID: reasonEntry.teacher_id,
          teacherName: reasonEntry.name || reasonEntry.assigned_id,
          status: "absent",
          reasonCode,
          reasonDetail: reasonDetail.trim() || undefined,
        },
      ]);
      setDraft((current) => {
        const next = { ...current };
        delete next[reasonEntry.teacher_id];
        return next;
      });
      track("attendance_submitted", {
        payload: { session_id: sessionID, queued: 1 },
      });
      await flushOutbox();
      if (outboxFor(sessionID).some((entry) => entry.teacherID === reasonEntry.teacher_id)) {
        setReasonError(t("absenceNotSavedYet"));
        return;
      }
      await reload();
      setReasonFor(null);
    } catch (cause) {
      setReasonError(cause instanceof Error ? cause.message : t("syncFailedRow"));
    } finally {
      setSavingReason(false);
    }
  }

  const back = (
    <button
      className={`${ghostButtonClass} mb-4`}
      onClick={() => navigate(pdDayDetailPath(sessionID))}
    >
      <ChevronLeft size={16} />
      {dayNumber ? t("pdDayOrdinal", { number: dayNumber }) : t("backToPDDay")}
    </button>
  );
  const title = (
    <h1 className="text-xl font-semibold tracking-tight text-app-ink">
      {dayNumber ? t("pdDayAttendanceOrdinal", { number: dayNumber }) : t("pdDayAttendance")}
    </h1>
  );

  if (loading) {
    return <Status variant="loading" title={t("loading")} message={t("loadingRoster")} />;
  }
  if (error || !data) {
    return (
      <>
        {back}
        <Status variant="error" title={t("rosterUnavailable")} message={error || t("noRoster")} />
      </>
    );
  }

  if (roster.length === 0) {
    return (
      <>
        {back}
        <div className="flex flex-col gap-4">
          {title}
          <Status variant="empty" title={t("emptyRoster")} message={t("emptyRosterHelp")} />
        </div>
      </>
    );
  }

  return (
    <div className="pb-28">
      {back}
      <div className="flex flex-col gap-4">
        {title}
        <AttendanceTally
          total={roster.length}
          present={present}
          absent={absent}
          unmarked={unmarked}
        />

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-app-ink">{t("teacherRoster")}</span>
          <span className="grid h-5 min-w-5 place-items-center rounded-full bg-app-field px-1.5 text-[11px] font-semibold text-app-copy">
            {roster.length}
          </span>
        </div>

        {locked ? (
          <p className="rounded-xl bg-app-field px-4 py-3 text-center text-sm font-medium text-app-copy">
            {t("attendanceLocked")}
          </p>
        ) : (
          <p className="px-1 text-xs text-app-muted">{t("attendanceFinalHint")}</p>
        )}

        <ul className="flex flex-col gap-2">
          {roster.map((entry) => (
            <RosterRow
              key={entry.teacher_id}
              entry={entry}
              status={statusOf(entry.teacher_id, entry.attendance)}
              dirty={Boolean(
                draft[entry.teacher_id] && draft[entry.teacher_id] !== entry.attendance
              )}
              sync={outbox.get(entry.teacher_id)}
              disabled={locked || saving || submitted(entry)}
              onChange={(status) => {
                if (status === "absent") openAbsenceReason(entry);
                else {
                  // A change of mind before submitting is recorded: how often a
                  // facilitator revises the register is part of how it was taken.
                  track("attendance_draft_changed", {
                    payload: {
                      session_id: sessionID,
                      teacher_id: entry.teacher_id,
                      from: statusOf(entry.teacher_id, entry.attendance),
                      to: status,
                    },
                  });
                  setDraft((current) => ({ ...current, [entry.teacher_id]: status }));
                }
              }}
              onRetry={() => {
                const queued = outbox.get(entry.teacher_id);
                if (queued?.status === "absent" && !queued.reasonCode) openAbsenceReason(entry);
                else void retryEntry(`${sessionID}:${entry.teacher_id}`);
              }}
            />
          ))}
        </ul>

        {inFlightCount > 0 && (
          <p className="rounded-md bg-accent-soft px-3 py-2 text-center text-sm font-medium text-accent">
            {t("attendanceSyncing", { n: inFlightCount })}
          </p>
        )}
        {failedEntries.length > 0 && (
          <p className="rounded-md bg-danger-bg px-3 py-2 text-center text-sm text-accent-strong">
            {t("attendancePartialError", {
              n: failedEntries.length,
              names: failedEntries.map((entry) => entry.teacherName).join(", "),
            })}
          </p>
        )}
      </div>

      {!locked && (
        <div className="fixed bottom-0 left-1/2 z-40 w-full max-w-md -translate-x-1/2 bg-app-canvas/90 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] backdrop-blur-md">
          <button
            type="button"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-accent text-base font-semibold text-content-on-accent shadow-sm transition-colors hover:bg-accent-hover disabled:bg-app-field disabled:text-app-icon disabled:shadow-none"
            onClick={() => void submit()}
            disabled={saving || pending.length === 0}
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <UserCheck size={18} />}
            {pending.length > 0
              ? t("submitAttendanceCount", { n: pending.length })
              : t("submitAttendance")}
          </button>
        </div>
      )}
      <FormModal
        open={Boolean(reasonEntry)}
        title={t("absenceReasonTitle", {
          name: reasonEntry?.name || reasonEntry?.assigned_id || "",
        })}
        message={t("absenceReasonHelp")}
        submitLabel={t("submitAbsenceReason")}
        busy={savingReason}
        disabled={!reasonCode || (reasonCode === "other" && !reasonDetail.trim())}
        error={reasonError}
        onSubmit={() => void submitAbsenceReason()}
        onCancel={() => setReasonFor(null)}
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-semibold text-app-ink">
            {t("absenceReasonLabel")}
          </legend>
          {absenceReasons.map((code) => (
            <label
              key={code}
              className="flex items-center gap-3 rounded-lg border border-app-line px-3 py-2 text-sm text-app-ink"
            >
              <input
                type="radio"
                name="absence-reason"
                value={code}
                checked={reasonCode === code}
                onChange={() => setReasonCode(code)}
                className="accent-accent"
              />
              {t(`absenceReason_${code}`)}
            </label>
          ))}
        </fieldset>
        {reasonCode && (
          <label className="flex flex-col gap-1 text-sm font-semibold text-app-ink">
            {reasonCode === "other" ? t("absenceReasonOtherPlaceholder") : t("absenceCommentLabel")}
            <textarea
              data-autofocus={reasonCode === "other" ? true : undefined}
              required={reasonCode === "other"}
              maxLength={500}
              rows={3}
              value={reasonDetail}
              onChange={(event) => setReasonDetail(event.target.value)}
              placeholder={
                reasonCode === "other"
                  ? t("absenceReasonOtherPlaceholder")
                  : t("absenceCommentLabel")
              }
              className="w-full rounded-xl border border-app-line bg-surface p-3 text-base font-normal text-app-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </label>
        )}
      </FormModal>
    </div>
  );
}
